'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { CloseRoundIcon } from '@/components/atoms/Icons';
import { Drawer } from '@/components/atoms/Drawer';
import UIButton from '@/components/atoms/UIButtons/UIButton';
import { useExplore } from './ExploreProvider';
import { MARKER_ICONS } from './markerIcons';
import { FILTER_KEYS, NESTED_FILTER_KEYS } from '../constants';
import Styles from './Explore.module.css';

// Toggle Switch Component - matches Figma design with pill-shaped toggle
function ToggleSwitch(t0) {
  const $ = _c(22);
  const {
    enabled,
    onChange,
    label,
    icon,
    ExpandedMenu,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `flex items-center justify-between p-3 rounded-[10px] ${ExpandedMenu ? "border-t rounded-none py-2.5 border-[#E2E2E2] pl-11" : ""}  ${className}`;
  let t3;
  if ($[0] !== icon) {
    t3 = icon && <div className="text-gray-600 w-6 h-6 flex items-center justify-center">{icon}</div>;
    $[0] = icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== label) {
    t4 = <span className="text-[#323232] text-base font-medium leading-[1.25em]">{label}</span>;
    $[2] = label;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <div className="flex items-center gap-2">{t3}{t4}</div>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== enabled || $[8] !== onChange) {
    t6 = () => onChange(!enabled);
    $[7] = enabled;
    $[8] = onChange;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `relative inline-flex h-[26px] w-[45px] items-center rounded-full transition-colors duration-200 ${enabled ? "bg-[#00EAFD]" : "bg-[#CCCCCC]"}`;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="sr-only">toggle</span>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = `inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-sm transition-transform duration-200 ${enabled ? "translate-x-[21px]" : "translate-x-[2px]"}`;
  let t10;
  if ($[11] !== t9) {
    t10 = <span className={t9} />;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== enabled || $[14] !== t10 || $[15] !== t6 || $[16] !== t7) {
    t11 = <button onClick={t6} aria-checked={enabled} role="switch" className={t7}>{t8}{t10}</button>;
    $[13] = enabled;
    $[14] = t10;
    $[15] = t6;
    $[16] = t7;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t2 || $[20] !== t5) {
    t12 = <div className={t2}>{t5}{t11}</div>;
    $[18] = t11;
    $[19] = t2;
    $[20] = t5;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}
function InlineSwitch(t0) {
  const $ = _c(14);
  const {
    enabled,
    onChange,
    ariaLabel
  } = t0;
  let t1;
  if ($[0] !== enabled || $[1] !== onChange) {
    t1 = () => onChange(!enabled);
    $[0] = enabled;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = `relative inline-flex h-[26px] w-[45px] items-center rounded-full transition-colors duration-200 ${enabled ? "bg-[#00EAFD]" : "bg-[#CCCCCC]"}`;
  let t3;
  if ($[3] !== ariaLabel) {
    t3 = <span className="sr-only">{ariaLabel}</span>;
    $[3] = ariaLabel;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow-sm transition-transform duration-200 ${enabled ? "translate-x-[21px]" : "translate-x-[2px]"}`;
  let t5;
  if ($[5] !== t4) {
    t5 = <span className={t4} />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== ariaLabel || $[8] !== enabled || $[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t5) {
    t6 = <button type="button" onClick={t1} aria-checked={enabled} aria-label={ariaLabel} role="switch" className={t2}>{t3}{t5}</button>;
    $[7] = ariaLabel;
    $[8] = enabled;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}

// Dropdown Component
function Dropdown(t0) {
  const $ = _c(26);
  const {
    label,
    options,
    selected,
    onChange,
    icon
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] !== icon) {
    t1 = icon && <div className="text-gray-600 w-6 h-6 flex items-center justify-center">{icon}</div>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-[#323232] text-base font-medium leading-[1.25em]">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isOpen) {
    t4 = () => setIsOpen(!isOpen);
    $[7] = isOpen;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = `w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <svg className={t5} fill="none" stroke="currentColor" viewBox="0 0 24 24">{t6}</svg>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t7) {
    t8 = <button onClick={t4} className="flex items-center space-x-1 text-gray-500">{t7}</button>;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t3 || $[16] !== t8) {
    t9 = <div className="flex items-center justify-between">{t3}{t8}</div>;
    $[15] = t3;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== isOpen || $[19] !== onChange || $[20] !== options || $[21] !== selected) {
    t10 = isOpen && <div className="mt-2 pl-9 space-y-2">{options.map(option => <label key={option.value} className="flex items-center space-x-2"><input type="checkbox" checked={selected.includes(option.value)} onChange={e => {
          if (e.target.checked) {
            onChange([...selected, option.value]);
          } else {
            onChange(selected.filter(v => v !== option.value));
          }
        }} className="rounded border-gray-300 text-cyan-400 focus:ring-cyan-400" /><span className="text-sm text-gray-700">{option.label}</span></label>)}</div>;
    $[18] = isOpen;
    $[19] = onChange;
    $[20] = options;
    $[21] = selected;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className="py-3">{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}

// Expandable Section Component - fully customizable via className prop
function ExpandableSection(t0) {
  const $ = _c(30);
  const {
    label,
    icon,
    children,
    defaultExpanded: t1,
    className: t2,
    headerControl
  } = t0;
  const defaultExpanded = t1 === undefined ? false : t1;
  const className = t2 === undefined ? "" : t2;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const t3 = `rounded-lg relative ${className}`;
  let t4;
  if ($[0] !== icon) {
    t4 = icon && <div className="text-gray-600 w-6 h-6 flex items-center justify-center">{icon}</div>;
    $[0] = icon;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== label) {
    t5 = <span className="text-[#323232] text-base font-medium leading-[1.25em]">{label}</span>;
    $[2] = label;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = `w-4 h-4 transition-transform text-gray-500 ${isExpanded ? "rotate-180" : ""}`;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== t6) {
    t8 = <svg className={t6} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">{t7}</svg>;
    $[5] = t6;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t8) {
    t9 = <div className="flex items-center gap-2 text-left">{t4}{t5}{t8}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== isExpanded) {
    t10 = () => setIsExpanded(!isExpanded);
    $[11] = isExpanded;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="sr-only">expand</span>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== isExpanded || $[15] !== t10) {
    t12 = <button type="button" onClick={t10} aria-expanded={isExpanded} className="w-full h-full absolute inset-0">{t11}</button>;
    $[14] = isExpanded;
    $[15] = t10;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== headerControl) {
    t13 = <div className="flex items-center gap-3">{headerControl}</div>;
    $[17] = headerControl;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13 || $[21] !== t9) {
    t14 = <div className="w-full flex items-center justify-between p-3">{t9}{t12}{t13}</div>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t9;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== children || $[24] !== isExpanded) {
    t15 = isExpanded && <div className="expanded-section pb-1">{children}</div>;
    $[23] = children;
    $[24] = isExpanded;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t14 || $[27] !== t15 || $[28] !== t3) {
    t16 = <div className={t3}>{t14}{t15}</div>;
    $[26] = t14;
    $[27] = t15;
    $[28] = t3;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  return t16;
}

/**
 * Hook to manage filter state and handlers
 * Exported for use by custom filter UIs that need the same logic
 * Follows hexagonal pattern - separates logic from presentation
 */
export function useFilterHandlers() {
  const $ = _c(38);
  const {
    filters,
    setFilters,
    getUniqueCommunities,
    getUniqueAttractionCategories,
    industriesData
  } = useExplore();
  let t0;
  if ($[0] !== getUniqueCommunities) {
    t0 = getUniqueCommunities();
    $[0] = getUniqueCommunities;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const communityOptions = t0;
  let t1;
  if ($[2] !== getUniqueAttractionCategories) {
    t1 = getUniqueAttractionCategories();
    $[2] = getUniqueAttractionCategories;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const attractionCategory = t1;
  let t2;
  if ($[4] !== industriesData?.filterOptions) {
    t2 = industriesData?.filterOptions || [];
    $[4] = industriesData?.filterOptions;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const promotionFilterOptions = t2;
  let t3;
  if ($[6] !== setFilters) {
    t3 = (key, value) => {
      setFilters({
        [key]: value
      });
    };
    $[6] = setFilters;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleFilterChange = t3;
  let t4;
  if ($[8] !== filters || $[9] !== setFilters) {
    t4 = (filterType, key_0, value_0, allOptions) => {
      if (key_0 === NESTED_FILTER_KEYS.ALL) {
        const allIds = value_0 ? allOptions.map(_temp) : [];
        setFilters({
          [filterType]: {
            [NESTED_FILTER_KEYS.ALL]: value_0,
            [NESTED_FILTER_KEYS.SELECTED]: allIds
          }
        });
      } else {
        if (key_0 === NESTED_FILTER_KEYS.SELECTED) {
          const allSelected = value_0.length === allOptions.length;
          setFilters({
            [filterType]: {
              [NESTED_FILTER_KEYS.ALL]: allSelected,
              [NESTED_FILTER_KEYS.SELECTED]: value_0
            }
          });
        } else {
          setFilters({
            [filterType]: {
              ...filters[filterType],
              [key_0]: value_0
            }
          });
        }
      }
    };
    $[8] = filters;
    $[9] = setFilters;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleNestedFilterChange = t4;
  let t5;
  if ($[11] !== filters || $[12] !== promotionFilterOptions || $[13] !== setFilters) {
    t5 = (key_1, value_1) => {
      if (key_1 === "all") {
        const categoryStates = {};
        promotionFilterOptions.forEach(opt_0 => {
          categoryStates[opt_0.key] = value_1;
        });
        setFilters({
          promotions: {
            all: value_1,
            ...categoryStates
          }
        });
      } else {
        const newPromotions = {
          ...filters.promotions,
          [key_1]: value_1
        };
        const allSubFiltersOn = promotionFilterOptions.every(opt_1 => newPromotions[opt_1.key] !== false);
        setFilters({
          promotions: {
            ...newPromotions,
            all: allSubFiltersOn
          }
        });
      }
    };
    $[11] = filters;
    $[12] = promotionFilterOptions;
    $[13] = setFilters;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handlePromotionChange = t5;
  let t6;
  if ($[15] !== attractionCategory || $[16] !== communityOptions || $[17] !== promotionFilterOptions || $[18] !== setFilters) {
    t6 = () => {
      const allCommunityIds = communityOptions.map(_temp2);
      const allAttractionCategoryIds = attractionCategory.map(_temp3);
      const promotionFilters = {
        all: true
      };
      promotionFilterOptions.forEach(opt_4 => {
        promotionFilters[opt_4.key] = true;
      });
      setFilters({
        hearts: true,
        communities: {
          all: true,
          selected: allCommunityIds
        },
        artists: true,
        attractions: {
          all: true,
          selected: allAttractionCategoryIds
        },
        events: true,
        promotions: promotionFilters
      });
    };
    $[15] = attractionCategory;
    $[16] = communityOptions;
    $[17] = promotionFilterOptions;
    $[18] = setFilters;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const resetFilters = t6;
  let t7;
  if ($[20] !== filters) {
    t7 = () => filters.hearts && filters.communities.all && filters.artists && filters.attractions.all && filters.events && filters.promotions.all;
    $[20] = filters;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const isShowAllEnabled = t7;
  let t8;
  if ($[22] !== attractionCategory || $[23] !== communityOptions || $[24] !== promotionFilterOptions || $[25] !== setFilters) {
    t8 = value_2 => {
      const allCommunityIds_0 = communityOptions.map(_temp4);
      const allAttractionCategoryIds_0 = attractionCategory.map(_temp5);
      const promotionFilters_0 = {
        all: value_2
      };
      promotionFilterOptions.forEach(opt_7 => {
        promotionFilters_0[opt_7.key] = value_2;
      });
      setFilters({
        hearts: value_2,
        communities: {
          all: value_2,
          selected: value_2 ? allCommunityIds_0 : []
        },
        artists: value_2,
        attractions: {
          all: value_2,
          selected: value_2 ? allAttractionCategoryIds_0 : []
        },
        events: value_2,
        promotions: promotionFilters_0
      });
    };
    $[22] = attractionCategory;
    $[23] = communityOptions;
    $[24] = promotionFilterOptions;
    $[25] = setFilters;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const handleShowAllToggle = t8;
  let t9;
  if ($[27] !== attractionCategory || $[28] !== communityOptions || $[29] !== filters || $[30] !== handleFilterChange || $[31] !== handleNestedFilterChange || $[32] !== handlePromotionChange || $[33] !== handleShowAllToggle || $[34] !== isShowAllEnabled || $[35] !== promotionFilterOptions || $[36] !== resetFilters) {
    t9 = {
      filters,
      communityOptions,
      attractionCategory,
      promotionFilterOptions,
      handleFilterChange,
      handleNestedFilterChange,
      handlePromotionChange,
      resetFilters,
      isShowAllEnabled,
      handleShowAllToggle
    };
    $[27] = attractionCategory;
    $[28] = communityOptions;
    $[29] = filters;
    $[30] = handleFilterChange;
    $[31] = handleNestedFilterChange;
    $[32] = handlePromotionChange;
    $[33] = handleShowAllToggle;
    $[34] = isShowAllEnabled;
    $[35] = promotionFilterOptions;
    $[36] = resetFilters;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  return t9;
}

/** Default style presets for FilterContent */
function _temp5(opt_6) {
  return opt_6.value;
}
function _temp4(opt_5) {
  return opt_5.value;
}
function _temp3(opt_3) {
  return opt_3.value;
}
function _temp2(opt_2) {
  return opt_2.value;
}
function _temp(opt) {
  return opt.value;
}
const FILTER_STYLES = {
  modal: {
    row: 'bg-[#F7F7F7]',
    nested: ''
  },
  panel: {
    row: 'bg-white',
    nested: ''
  }
};

/**
 * Shared filter content component
 * Used by both FilterModal and FilterPanel
 *
 * @param {boolean} showHeader - Show header with reset button
 * @param {string} className - Container className
 * @param {string} variant - Style preset: 'modal' or 'panel'
 * @param {object} styles - Custom styles override { row: string, nested: string }
 *
 * For full customization, use useFilterHandlers() hook directly with your own UI
 */
export function FilterContent(t0) {
  const $ = _c(108);
  const {
    showHeader: t1,
    className: t2,
    variant: t3,
    styles: t4,
    toggleWrapperClass: t5
  } = t0;
  const showHeader = t1 === undefined ? true : t1;
  const className = t2 === undefined ? "" : t2;
  const variant = t3 === undefined ? "modal" : t3;
  let t6;
  if ($[0] !== t4) {
    t6 = t4 === undefined ? {} : t4;
    $[0] = t4;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const customStyles = t6;
  const toggleWrapperClass = t5 === undefined ? "" : t5;
  const {
    sortMode,
    requestSortMode
  } = useExplore();
  const {
    filters,
    communityOptions,
    attractionCategory,
    promotionFilterOptions,
    handleFilterChange,
    handleNestedFilterChange,
    handlePromotionChange,
    isShowAllEnabled,
    handleShowAllToggle
  } = useFilterHandlers();
  const t7 = FILTER_STYLES[variant];
  let t8;
  if ($[2] !== customStyles || $[3] !== t7) {
    t8 = {
      ...t7,
      ...customStyles
    };
    $[2] = customStyles;
    $[3] = t7;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const styles = t8;
  let t9;
  if ($[5] !== requestSortMode || $[6] !== sortMode || $[7] !== variant) {
    t9 = variant === "modal" && <div className="xl:pr-9 pr-6"><div className="relative"><div className="flex items-center justify-between pb-3"><h2 className="text-2xl font-semibold text-[#323232] leading-[1]">Sort By</h2></div><select value={sortMode} onChange={event => requestSortMode(event.target.value)} className="rounded-[10px] border mb-[37px] w-full border-[#E0E0E0] bg-white px-[16px] py-[10.5px] text-[16px] font-[500] text-[#323232] appearance-none cursor-pointer"><option value="alphabetical">Alphabetical</option><option value="distance">Distance</option></select><span className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="6" viewBox="0 0 11 6" fill="none"><path d="M10.5625 1.0625L6.03125 5.59375C5.92708 5.69792 5.82292 5.77083 5.71875 5.8125C5.61458 5.85417 5.5 5.875 5.375 5.875C5.25 5.875 5.13542 5.85417 5.03125 5.8125C4.92708 5.77083 4.82292 5.69792 4.71875 5.59375L0.1875 1.0625C0.125 0.999999 0.0781248 0.932291 0.0468748 0.859374C0.0156248 0.786458 -2.25842e-07 0.708333 -2.29485e-07 0.624999C-2.3677e-07 0.458333 0.0572914 0.312499 0.171875 0.187499C0.286458 0.062499 0.4375 -9.72798e-07 0.625 -9.80994e-07L10.125 -1.39625e-06C10.3125 -1.40445e-06 10.4635 0.0624986 10.5781 0.187499C10.6927 0.312499 10.75 0.458332 10.75 0.624999C10.75 0.666666 10.6875 0.812499 10.5625 1.0625Z" fill="#989898" /></svg></span></div></div>;
    $[5] = requestSortMode;
    $[6] = sortMode;
    $[7] = variant;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== showHeader) {
    t10 = showHeader && <div className="flex items-center justify-between pb-3"><h2 className="text-2xl font-semibold text-[#323232] leading-[1]">All Filters</h2></div>;
    $[9] = showHeader;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = `${toggleWrapperClass}`;
  let t12;
  if ($[11] !== isShowAllEnabled) {
    t12 = isShowAllEnabled();
    $[11] = isShowAllEnabled;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== handleShowAllToggle || $[14] !== styles.row || $[15] !== t12) {
    t13 = <ToggleSwitch enabled={t12} onChange={handleShowAllToggle} label="Show All" className={styles.row} />;
    $[13] = handleShowAllToggle;
    $[14] = styles.row;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== handleFilterChange) {
    t14 = value => handleFilterChange("hearts", value);
    $[17] = handleFilterChange;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <img src={MARKER_ICONS.heart.src} alt={MARKER_ICONS.heart.alt} className="w-5 h-5" />;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== filters.hearts || $[21] !== styles.row || $[22] !== t14) {
    t16 = <ToggleSwitch enabled={filters.hearts} onChange={t14} label={MARKER_ICONS.heart.label} icon={t15} className={styles.row} />;
    $[20] = filters.hearts;
    $[21] = styles.row;
    $[22] = t14;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <img src={MARKER_ICONS.community.src} alt={MARKER_ICONS.community.alt} className="w-5 h-5" />;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const t18 = styles.row;
  const t19 = filters[FILTER_KEYS.COMMUNITIES];
  let t20;
  if ($[25] !== communityOptions || $[26] !== handleNestedFilterChange) {
    t20 = value_0 => handleNestedFilterChange(FILTER_KEYS.COMMUNITIES, NESTED_FILTER_KEYS.ALL, value_0, communityOptions);
    $[25] = communityOptions;
    $[26] = handleNestedFilterChange;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t19.all || $[29] !== t20) {
    t21 = <InlineSwitch enabled={t19.all} onChange={t20} ariaLabel="All Communities" />;
    $[28] = t19.all;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  const t22 = `space-y-2 ${styles.nested}`;
  let t23;
  if ($[31] !== communityOptions || $[32] !== filters || $[33] !== handleNestedFilterChange) {
    t23 = communityOptions.map(option => <ToggleSwitch key={option.value} enabled={filters[FILTER_KEYS.COMMUNITIES].selected.includes(option.value)} onChange={value_1 => {
      const newSelected = value_1 ? [...filters[FILTER_KEYS.COMMUNITIES].selected, option.value] : filters[FILTER_KEYS.COMMUNITIES].selected.filter(v => v !== option.value);
      handleNestedFilterChange(FILTER_KEYS.COMMUNITIES, NESTED_FILTER_KEYS.SELECTED, newSelected, communityOptions);
    }} ExpandedMenu={true} label={option.label} />);
    $[31] = communityOptions;
    $[32] = filters;
    $[33] = handleNestedFilterChange;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] !== t22 || $[36] !== t23) {
    t24 = <div className={t22}>{t23}</div>;
    $[35] = t22;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== styles.row || $[39] !== t21 || $[40] !== t24) {
    t25 = <ExpandableSection label={MARKER_ICONS.community.label} defaultExpanded={false} icon={t17} className={t18} headerControl={t21}>{t24}</ExpandableSection>;
    $[38] = styles.row;
    $[39] = t21;
    $[40] = t24;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== handleFilterChange) {
    t26 = value_2 => handleFilterChange("artists", value_2);
    $[42] = handleFilterChange;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <img src={MARKER_ICONS.artist.src} alt={MARKER_ICONS.artist.alt} className="w-5 h-5" />;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== filters.artists || $[46] !== styles.row || $[47] !== t26) {
    t28 = <ToggleSwitch enabled={filters.artists} onChange={t26} label={MARKER_ICONS.artist.label} icon={t27} className={styles.row} />;
    $[45] = filters.artists;
    $[46] = styles.row;
    $[47] = t26;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <img src={MARKER_ICONS.attraction.src} alt={MARKER_ICONS.attraction.alt} className="w-5 h-5" />;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  const t30 = styles.row;
  const t31 = filters[FILTER_KEYS.ATTRACTIONS];
  let t32;
  if ($[50] !== attractionCategory || $[51] !== handleNestedFilterChange) {
    t32 = value_3 => handleNestedFilterChange(FILTER_KEYS.ATTRACTIONS, NESTED_FILTER_KEYS.ALL, value_3, attractionCategory);
    $[50] = attractionCategory;
    $[51] = handleNestedFilterChange;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  let t33;
  if ($[53] !== t31.all || $[54] !== t32) {
    t33 = <InlineSwitch enabled={t31.all} onChange={t32} ariaLabel="All Attractions" />;
    $[53] = t31.all;
    $[54] = t32;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  const t34 = `space-y-2 ${styles.nested}`;
  let t35;
  if ($[56] !== attractionCategory || $[57] !== filters || $[58] !== handleNestedFilterChange) {
    t35 = attractionCategory.map(option_0 => <ToggleSwitch key={option_0.value} enabled={filters[FILTER_KEYS.ATTRACTIONS].selected.includes(option_0.value)} onChange={value_4 => {
      const newSelected_0 = value_4 ? [...filters[FILTER_KEYS.ATTRACTIONS].selected, option_0.value] : filters[FILTER_KEYS.ATTRACTIONS].selected.filter(v_0 => v_0 !== option_0.value);
      handleNestedFilterChange(FILTER_KEYS.ATTRACTIONS, NESTED_FILTER_KEYS.SELECTED, newSelected_0, attractionCategory);
    }} ExpandedMenu={true} label={option_0.label} />);
    $[56] = attractionCategory;
    $[57] = filters;
    $[58] = handleNestedFilterChange;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== t34 || $[61] !== t35) {
    t36 = <div className={t34}>{t35}</div>;
    $[60] = t34;
    $[61] = t35;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  let t37;
  if ($[63] !== styles.row || $[64] !== t33 || $[65] !== t36) {
    t37 = <ExpandableSection label={MARKER_ICONS.attraction.label} defaultExpanded={false} icon={t29} className={t30} headerControl={t33}>{t36}</ExpandableSection>;
    $[63] = styles.row;
    $[64] = t33;
    $[65] = t36;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] !== handleFilterChange) {
    t38 = value_5 => handleFilterChange("events", value_5);
    $[67] = handleFilterChange;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <img src={MARKER_ICONS.event.src} alt={MARKER_ICONS.event.alt} className="w-5 h-5" />;
    $[69] = t39;
  } else {
    t39 = $[69];
  }
  let t40;
  if ($[70] !== filters.events || $[71] !== styles.row || $[72] !== t38) {
    t40 = <ToggleSwitch enabled={filters.events} onChange={t38} label={MARKER_ICONS.event.label} icon={t39} className={styles.row} />;
    $[70] = filters.events;
    $[71] = styles.row;
    $[72] = t38;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  let t41;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <img src={MARKER_ICONS.promotion.src} alt={MARKER_ICONS.promotion.alt} className="w-5 h-5" />;
    $[74] = t41;
  } else {
    t41 = $[74];
  }
  const t42 = styles.row;
  let t43;
  if ($[75] !== handlePromotionChange) {
    t43 = value_6 => handlePromotionChange("all", value_6);
    $[75] = handlePromotionChange;
    $[76] = t43;
  } else {
    t43 = $[76];
  }
  let t44;
  if ($[77] !== filters.promotions.all || $[78] !== t43) {
    t44 = <InlineSwitch enabled={filters.promotions.all} onChange={t43} ariaLabel="All Promotions" />;
    $[77] = filters.promotions.all;
    $[78] = t43;
    $[79] = t44;
  } else {
    t44 = $[79];
  }
  const t45 = `${styles.nested}`;
  let t46;
  if ($[80] !== filters.promotions || $[81] !== handlePromotionChange || $[82] !== promotionFilterOptions) {
    let t47;
    if ($[84] !== filters.promotions || $[85] !== handlePromotionChange) {
      t47 = option_1 => <ToggleSwitch key={option_1.key} enabled={filters.promotions[option_1.key] !== false} onChange={value_7 => handlePromotionChange(option_1.key, value_7)} label={option_1.label} ExpandedMenu={true} />;
      $[84] = filters.promotions;
      $[85] = handlePromotionChange;
      $[86] = t47;
    } else {
      t47 = $[86];
    }
    t46 = promotionFilterOptions.map(t47);
    $[80] = filters.promotions;
    $[81] = handlePromotionChange;
    $[82] = promotionFilterOptions;
    $[83] = t46;
  } else {
    t46 = $[83];
  }
  let t47;
  if ($[87] !== t45 || $[88] !== t46) {
    t47 = <div className={t45}>{t46}</div>;
    $[87] = t45;
    $[88] = t46;
    $[89] = t47;
  } else {
    t47 = $[89];
  }
  let t48;
  if ($[90] !== styles.row || $[91] !== t44 || $[92] !== t47) {
    t48 = <ExpandableSection label={MARKER_ICONS.promotion.label} defaultExpanded={false} icon={t41} className={t42} headerControl={t44}>{t47}</ExpandableSection>;
    $[90] = styles.row;
    $[91] = t44;
    $[92] = t47;
    $[93] = t48;
  } else {
    t48 = $[93];
  }
  let t49;
  if ($[94] !== t11 || $[95] !== t13 || $[96] !== t16 || $[97] !== t25 || $[98] !== t28 || $[99] !== t37 || $[100] !== t40 || $[101] !== t48) {
    t49 = <div className={t11}>{t13}{t16}{t25}{t28}{t37}{t40}{t48}</div>;
    $[94] = t11;
    $[95] = t13;
    $[96] = t16;
    $[97] = t25;
    $[98] = t28;
    $[99] = t37;
    $[100] = t40;
    $[101] = t48;
    $[102] = t49;
  } else {
    t49 = $[102];
  }
  let t50;
  if ($[103] !== className || $[104] !== t10 || $[105] !== t49 || $[106] !== t9) {
    t50 = <div className={className}>{t9}{t10}{t49}</div>;
    $[103] = className;
    $[104] = t10;
    $[105] = t49;
    $[106] = t9;
    $[107] = t50;
  } else {
    t50 = $[107];
  }
  return t50;
}

/**
 * Filter Modal - uses common Modal component
 * Used on map/photos view (mobile and desktop)
 */
export function FilterModal(t0) {
  const $ = _c(14);
  const {
    isOpen,
    onClose
  } = t0;
  const [isMobile, setIsMobile] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const checkMobile = () => setIsMobile(window.innerWidth < 768);
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = isMobile ? "bottom" : "right";
  const t4 = isMobile ? "rounded-t-2xl" : "2xl:w-[510px] lg:w-[450px] md:w-[370px] h-full rounded-l-[14px]";
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CloseRoundIcon className="md:w-5 md:h-5 w-3.5 h-3.5 text-[#323232]" />;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== onClose) {
    t6 = <UIButton ariaLabel="Close" onClick={onClose} rounded="full" variant="primaryTheme" className="cursor-pointer top-3 xl:right-4 right-3 flex justify-center items-center absolute z-20  xl:w-12 xl:h-12 md:w-10.5 md:h-10.5 w-9.5 h-9.5 !px-0 !py-0">{t5}</UIButton>;
    $[3] = onClose;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FilterContent showHeader={true} className="space-y-1" toggleWrapperClass={`space-y-1 overflow-y-auto xl:pr-9 pr-6 pb-5 ${Styles.FilterContentWrapper}`} />;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t6) {
    t8 = <div className="2xl:pt-8 pt-6 xl:pl-9 pl-6">{t6}{t7}</div>;
    $[6] = t6;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== isOpen || $[9] !== onClose || $[10] !== t3 || $[11] !== t4 || $[12] !== t8) {
    t9 = <Drawer isOpen={isOpen} onClose={onClose} direction={t3} ariaLabel="Filter options" className={t4} closeOnOverlayClick={true}>{t8}</Drawer>;
    $[8] = isOpen;
    $[9] = onClose;
    $[10] = t3;
    $[11] = t4;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}

// Composable filter components for reuse
export { ToggleSwitch, Dropdown, ExpandableSection };